/**
 * Clarity custom events + user tags.
 *
 * Microsoft Clarity supports two low-cost APIs that hugely improve
 * the "which sessions should I actually watch?" workflow:
 *
 *   clarity('event', 'name')           — timeline marker on the session
 *   clarity('set', 'key', 'value')     — a filterable/segment-able tag
 *                                        that sticks to the whole session
 *
 * We wrap both so:
 *   • Every call is silently no-op if Clarity hasn't loaded (consent
 *     not yet given, ad-blocker present, dev environment).
 *   • Internal traffic (admin / preview host / Emergent emails) is
 *     dropped BEFORE we call Clarity, keeping the founder's own QA
 *     out of session recordings and heatmaps.
 *   • The full list of event names is centralized in `EVENTS` so the
 *     Clarity dashboard segment definitions and this file stay in sync.
 *
 * How the founder uses this in the Clarity dashboard:
 *   1. Filter → Custom tags → "user_role" = "real_caregiver"  →
 *      strips out every admin / test / Emergent session in one click.
 *   2. Filter → Custom events → "signup_completed" AND NOT
 *      "feedback_submitted"  →  the "signed up but never gave feedback"
 *      cohort you can watch in 5 min.
 *   3. Filter → Dead clicks OR rage clicks + user_role=real_caregiver →
 *      the pure-signal frustration segment.
 */
import { isInternalUser } from "@/lib/internalTraffic";

export const CLARITY_EVENTS = {
    // Auth
    signup_started: "signup_started",
    signup_completed: "signup_completed",
    login_success: "login_success",
    demo_login_used: "demo_login_used",
    magic_link_requested: "magic_link_requested",
    magic_link_consumed: "magic_link_consumed",

    // Product usage
    roadmap_opened: "roadmap_opened",
    ai_chat_opened: "ai_chat_opened",
    ai_message_sent: "ai_message_sent",
    about_me_opened: "about_me_opened",
    care_navigator_opened: "care_navigator_opened",
    care_summary_generated: "care_summary_generated",
    document_uploaded: "document_uploaded",

    // Feedback
    feedback_opened: "feedback_opened",
    feedback_started: "feedback_started",
    feedback_submitted: "feedback_submitted",

    // Content
    guide_page_opened: "guide_page_opened",
    resource_clicked: "resource_clicked",
    article_opened: "article_opened",

    // Partner / booking
    partner_inquiry_opened: "partner_inquiry_opened",
    partner_inquiry_submitted: "partner_inquiry_submitted",
    demo_call_booked: "demo_call_booked",

    // Errors / friction
    error_seen: "error_seen",
    dead_click: "dead_click",
};

function _clarity() {
    if (typeof window === "undefined") return null;
    return typeof window.clarity === "function" ? window.clarity : null;
}

/**
 * Fire a timeline marker for a session. Shows up in the session
 * recording as a bookmark ("2:14 · signup_completed") so the founder
 * can jump straight to the interesting moment instead of scrubbing.
 * No-op if Clarity hasn't loaded or the user is internal traffic.
 */
export function setClarityEvent(name) {
    try {
        if (!name) return;
        if (typeof window === "undefined") return;
        if (isInternalUser()) return;
        const c = _clarity();
        if (c) c("event", String(name));
    } catch { /* Clarity must never throw */ }
}

/**
 * Attach a persistent, filterable tag to the whole session. Values
 * are visible in the Clarity Filters panel — the founder can build a
 * segment like `user_role = real_caregiver AND feedback_submitted = no`
 * without touching code.
 */
export function setClarityTag(key, value) {
    try {
        if (!key || value === undefined || value === null) return;
        if (typeof window === "undefined") return;
        if (isInternalUser()) return;
        const c = _clarity();
        if (c) c("set", String(key), String(value));
    } catch { /* Clarity must never throw */ }
}

/**
 * Identify + tag the current user on Clarity. Called once auth loads.
 * Tags applied:
 *   user_role   = admin | test | real_caregiver | prolific_tester | anon
 *   user_status = account status bucket
 *
 * Privacy review (Jun 2026): NO user identifiers are sent as raw tags.
 * The previous user_email / user_id tags were removed — Clarity now only
 * receives non-identifying role/status buckets. Session stitching still
 * works via clarity('identify', id) because Clarity hashes the custom-id
 * CLIENT-SIDE before it ever leaves the browser (per Microsoft docs).
 */
export function identifyClarityUser(user) {
    try {
        if (typeof window === "undefined") return;
        if (!user || !user.id) {
            setClarityTag("user_role", "anon");
            return;
        }
        const role = user.role === "admin"
            ? "admin"
            : user.tester_source === "prolific"
                ? "prolific_tester"
                : user.tester_code
                    ? "test_user"
                    : "real_caregiver";
        setClarityTag("user_role", role);
        setClarityTag("user_status", user.status || "unknown");
        // Clarity identify() hashes the id in-browser before sending —
        // enables multi-tab session stitching without exposing the raw id.
        const c = _clarity();
        if (c) c("identify", user.id, "", "", role);
    } catch { /* noop */ }
}
